import { ListResponseModel, toQueryString } from ".";
import {
	AdmimCustomerDetailsModel,
	AdminCustomerModel,
	CreditEngineScore,
	CustomerDashboardModel,
	CustomerPaymentOptions,
	CustomerQuery,
	CustomersOverview,
	CustomerType,
} from "../model/customer/customer";
import { LoanDetails, FulfilledLoans, OrderQuery } from "../model/orders/orders";
import { CustomerPaymentHistory } from "../model/payments/payments";
import { apiSlice } from "../stores/base/api-slice";
import { updateUsername } from "../stores/slices/current-user-slice";
import { FileFromObjectUrl } from "../model/common/file-from-object-url";
import { IdentityInformation } from "../model/IndentityInformation";
import {
	BvnDetailsResponse,
	BvnInitializationResponse,
	BvnVerificationResponse,
	CustomerRegistrationPersonalInformation,
} from "../stores/slices/customer-registration-slice";
import { Transaction } from "../model/transaction";

const prepareFileForUpload = async (file: FileFromObjectUrl | undefined) => {
	if (file?.url) {
		const blob = await fetch(file.url).then(r => r.blob());
		return new File([blob], file.name, { type: blob.type });
	}
	return undefined;
};

export const customerDashboardEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getCustomerDashboardInformation: builder.query<CustomerDashboardModel, void>({
			providesTags: ["CustomerDashboard"],
			query: () => ({
				url: "/customer/dashboard",
				method: "GET",
			}),
		}),
		getTransactions: builder.query<ListResponseModel<Array<CustomerPaymentHistory>>, CustomerQuery>({
			providesTags: ["Transactions"],
			query: query => ({
				url: "/customer/transactions" + toQueryString(query),
				method: "GET",
			}),
		}),
		getCustomer: builder.query<ListResponseModel<Array<AdminCustomerModel>>, CustomerQuery>({
			query: query => ({
				url: "/user/customer/page" + toQueryString(query),
				method: "GET",
			}),
		}),
		getCustomerForUtilityAdmin: builder.query<ListResponseModel<Array<AdminCustomerModel>>, CustomerQuery>({
			query: query => ({
				url: "/customer/for-utility-admin" + toQueryString(query),
				method: "GET",
			}),
		}),
		exportCustomersForUtilityAdmin: builder.mutation<void, CustomerQuery>({
			query: query => ({
				url: "/customer/for-utility-admin/export" + toQueryString(query),
				method: "POST",
			}),
		}),
		getCustomerDetailsForUtilityAdmin: builder.query<AdminCustomerModel, string>({
			providesTags: ["EnergyAccount"],
			query: userId => ({
				url: `/customer/for-utility-admin/${userId}`,
				method: "GET",
			}),
		}),
		getCustomerDetail: builder.query<AdmimCustomerDetailsModel, string>({
			query: customerId => ({
				url: `/customer/${customerId}/details`,
				method: "GET",
			}),
		}),
		getCustomerFulfilledLoans: builder.query<ListResponseModel<Array<FulfilledLoans>>, OrderQuery>({
			query: query => ({
				url: `/customer/${query?.customerId}/details/fulfilled-loans` + toQueryString({ Page: query?.Page, PageSize: query?.PageSize }),
				method: "GET",
			}),
		}),
		getCustomerActiveLoanDetails: builder.query<LoanDetails, string>({
			query: customerId => ({
				url: `/customer/${customerId}/details/active-loan-details`,
				method: "GET",
			}),
		}),
		getCustomerLoanRepaymentDetails: builder.query<ListResponseModel<Array<CustomerPaymentHistory>>, OrderQuery>({
			query: query => ({
				url: `/customer/${query?.customerId}/details/repayments` + toQueryString({ Page: query?.Page, PageSize: query?.PageSize }),
				method: "GET",
			}),
		}),
		getCustomerOnboardingStatus: builder.query<Array<{ type: string; date: string }>, string>({
			query: customerId => ({
				url: `/customer/${customerId}/details/onboarding-statuses`,
				method: "GET",
			}),
		}),
		getCustomerMandates: builder.query<
			{
				cardPaymentStatus: string;
				directDebitMandateStatus: string;
				accountBalance: number;
				accountBalanceUpdatedAt: string;
			},
			string
		>({
			query: customerId => ({
				url: `/customer/${customerId}/details/onboarding-statuses`,
				method: "GET",
			}),
		}),
		getCustomerMandatesV2: builder.query<
			{
				cardPaymentStatus: string;
				directDebitMandateStatus: string;
				accountBalance: number;
				accountBalanceUpdatedAt: string;
			},
			string
		>({
			query: customerId => ({
				url: `/customer/${customerId}/details/debits-mandate`,
				method: "GET",
			}),
		}),
		initiateSignup: builder.mutation<{ customerSignupId: string }, { email: string; firstName: string; lastName: string; password: string }>({
			query: request => ({
				url: `/customer/v2/registration/initiate`,
				method: "POST",
				body: request,
			}),
		}),
		verifyOtp: builder.mutation<{ token: string; userType: string; refreshToken: string }, { customerSignupId: string; otp: string }>({
			query: request => ({
				url: `/customer/v2/registration/verify`,
				method: "POST",
				body: request,
			}),
		}),
		resendOtp: builder.mutation<void, { customerSignupId: string }>({
			query: request => ({
				url: `/customer/v2/registration/resend`,
				method: "POST",
				body: request,
			}),
		}),
		getCustomerInfo: builder.query<CustomerType, void>({
			providesTags: ["CustomerInfo"],
			query: () => ({
				url: "/customer/info",
				method: "GET",
			}),
			onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
				try {
					const { data } = await queryFulfilled;
					dispatch(updateUsername({ userName: `${data?.firstName} ${data?.lastName ?? ""}` }));
				} catch (error) {
					console.error("Error fetching customer info:", error);
				}
			},
		}),
		getCustomerPaymentOptions: builder.query<CustomerPaymentOptions, void>({
			query: () => ({
				url: "/customer/payment-options",
				method: "GET",
			}),
		}),

		initiateVerifyPhoneNumber: builder.mutation<void, { phoneNumber: string }>({
			query: ({ phoneNumber }) => ({
				url: "customer/registration/initiate-phone-verification",
				method: "POST",
				body: { phoneNumber },
			}),
		}),

		validatePhoneNumber: builder.mutation<void, { token: string }>({
			query: ({ token }) => ({
				url: "customer/registration/validate-phone",
				method: "POST",
				body: { otp: token },
			}),
		}),

		updatePersonalInformation: builder.mutation<void, { personalInformation: CustomerRegistrationPersonalInformation }>({
			query: () => ({
				url: "customer/registration/update-personal-information",
				method: "POST",
			}),
			async onQueryStarted({ personalInformation }, { dispatch, queryFulfilled, getState }) {
				try {
					const form = new FormData();
					form.append("firstName", personalInformation.firstName!);
					form.append("lastName", personalInformation.lastName!);
					form.append("address", personalInformation.address!);
					form.append("state", personalInformation.state!);
					form.append("city", personalInformation.city!);
					form.append("meterNumber", personalInformation.meterNumber!);
					form.append("electricityDistributionCompany", personalInformation.electricityDistributionCompany!);

					const addressProofFile = await prepareFileForUpload(personalInformation.addressProofFile);
					if (addressProofFile) {
						form.append("addressProof", addressProofFile);
					}

					const response = await fetch(`${process.env.ZEPP_APP_SERVER_URL}/api/customer/registration/update-personal-information`, {
						method: "POST",
						body: form,
						headers: {
							Authorization: `Bearer ${sessionStorage.getItem("token")}`,
						},
					});

					if (!response.ok) {
						throw new Error("Failed to update personal information");
					}

					// Let the original query resolve
					await queryFulfilled;
				} catch (error) {
					console.error("Error updating personal information:", error);
					throw error;
				}
			},
		}),

		updateEmploymentInformation: builder.mutation<
			void,
			{
				employmentInformation: {
					companyName: string;
					jobTitle: string;
					joinDate: Date;
					employmentContract: FileFromObjectUrl;
					employmentType: string;
					linkedInPage?: string;
				};
			}
		>({
			query: () => ({
				url: "customer/registration/update-employment-information",
				method: "POST",
			}),
			async onQueryStarted({ employmentInformation }, { dispatch, queryFulfilled }) {
				try {
					const form = new FormData();
					form.append("companyName", employmentInformation.companyName);
					form.append("jobTitle", employmentInformation.jobTitle);
					form.append("joinDate", employmentInformation.joinDate.toISOString());
					form.append("employmentType", employmentInformation.employmentType);
					if (employmentInformation.linkedInPage) {
						form.append("linkedInPage", employmentInformation.linkedInPage);
					}

					const employmentContractFile = await prepareFileForUpload(employmentInformation.employmentContract);
					if (employmentContractFile) {
						form.append("employmentContract", employmentContractFile);
					}

					const response = await fetch(`${process.env.ZEPP_APP_SERVER_URL}/api/customer/registration/update-employment-information`, {
						method: "POST",
						body: form,
						headers: {
							Authorization: `Bearer ${sessionStorage.getItem("token")}`,
						},
					});

					if (!response.ok) {
						throw new Error("Failed to update employment information");
					}

					await queryFulfilled;
				} catch (error) {
					console.error("Error updating employment information:", error);
					throw error;
				}
			},
		}),

		updateIdentityInformation: builder.mutation<void, IdentityInformation>({
			query: () => ({
				url: "customer/registration/update-identity-information",
				method: "POST",
			}),
			async onQueryStarted({ identityPhoto, identityType, identityNumber }, { dispatch, queryFulfilled }) {
				try {
					const form = new FormData();
					form.append("identityType", identityType);
					form.append("identityNumber", identityNumber);

					const identityPhotoFile = await prepareFileForUpload(identityPhoto);
					if (identityPhotoFile) {
						form.append("identityPhoto", identityPhotoFile);
					}

					const response = await fetch(`${process.env.ZEPP_APP_SERVER_URL}/api/customer/registration/update-identity-information`, {
						method: "POST",
						body: form,
						headers: {
							Authorization: `Bearer ${sessionStorage.getItem("token")}`,
						},
					});

					if (!response.ok) {
						throw new Error("Failed to update identity information");
					}

					await queryFulfilled;
				} catch (error) {
					console.error("Error updating identity information:", error);
					throw error;
			  }
			},
		}),

		initializeBvn: builder.mutation<BvnInitializationResponse, { bvn: string; scope: string }>({
			query: ({ bvn, scope }) => ({
				url: "customer/bvn/initiate",
				method: "POST",
				body: { bvn, scope },
			}),
		}),

		verifyBvn: builder.mutation<BvnVerificationResponse, { method: string; phoneNumber: string; sessionId: string }>({
			query: ({ method, phoneNumber, sessionId }) => ({
				url: "customer/bvn/verify",
				method: "POST",
				body: { method, phoneNumber, sessionId },
			}),
		}),

		detailsBvn: builder.mutation<BvnDetailsResponse, { otp: string; sessionId: string }>({
			query: ({ otp, sessionId }) => ({
				url: "customer/bvn/details",
				method: "POST",
				body: { otp, sessionId },
			}),
		}),

		updateMono: builder.mutation<void, { monoTemporaryToken: string }>({
			query: ({ monoTemporaryToken }) => ({
				url: "customer/registration/save-mono",
				method: "POST",
				body: { monoTemporaryToken },
			}),
		}),

		getCreditEngineScore: builder.query<CreditEngineScore, { customerId: string; from: string }>({
			query: ({ customerId, from }) => ({
				url: "customer/credit-engine",
				method: "GET",
				params: { customerId, from },
			}),
		}),
		
		getCustomersOverview: builder.query<CustomersOverview, { From?: string; To?: string }>({
			query: (params) => ({
				url: "customer/utility/get-overview" + toQueryString(params),
				method: "GET",
			}),
		}),
		setCustomerBlacklisted: builder.mutation<string, { userId: string; isBlacklisted: boolean }>({
			query: ({ userId, isBlacklisted }) => ({
				url: `/user/users/${userId}/blacklist`,
				method: "PUT",
				body: { isBlacklisted },
			}),
		}),
	}),
});

export const {
	useLazyGetCustomerDashboardInformationQuery,
	useLazyGetCustomerQuery,
	useLazyGetTransactionsQuery,
	useLazyGetCustomerDetailQuery,
	useLazyGetCustomerActiveLoanDetailsQuery,
	useLazyGetCustomerLoanRepaymentDetailsQuery,
	useLazyGetCustomerFulfilledLoansQuery,
	useLazyGetCustomerOnboardingStatusQuery,
	useLazyGetCustomerMandatesQuery,
	useLazyGetCustomerMandatesV2Query,
	useLazyGetCustomerInfoQuery,
	useInitiateSignupMutation,
	useVerifyOtpMutation,
	useResendOtpMutation,
	useLazyGetCustomerPaymentOptionsQuery,
	useInitiateVerifyPhoneNumberMutation,
	useValidatePhoneNumberMutation,
	useUpdatePersonalInformationMutation,
	useUpdateEmploymentInformationMutation,
	useUpdateIdentityInformationMutation,
	useInitializeBvnMutation,
	useVerifyBvnMutation,
	useDetailsBvnMutation,
	useUpdateMonoMutation,
	useGetCreditEngineScoreQuery,
	useLazyGetCreditEngineScoreQuery,
	useLazyGetCustomerForUtilityAdminQuery,
	useExportCustomersForUtilityAdminMutation,
	useGetCustomerDetailsForUtilityAdminQuery,
	useLazyGetCustomersOverviewQuery,
	useSetCustomerBlacklistedMutation,
} = customerDashboardEndpoints;
